import axios from 'axios';
import { apiCall } from '../utils/apiUtils';

// Create an axios instance with default configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/ap/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized responses
    if (error.response && error.response.status === 401) {
      // Clear token and reload the page to reset the app state
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth service functions
export const AuthService = {
  login: (username: string, password: string) => {
    console.log('Sending login request with:', { username, password });
    return api.post('/auth/login', { 
      username, 
      password 
    });
  },
  
  // Secondary legacy login via PHP bridge (api.php -> Node service)
  PHPcodeLogin: (username: string, password: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      try {
        apiCall('/Login', { username, password }, (res: any) => {
          resolve(res);
        }, 'POST');
      } catch (e) {
        reject(e);
      }
    });
  },
  
  register: (userData: any) => {
    return api.post('/auth/register', userData);
  },
  
  getUserProfile: () => {
    return api.get('/auth/profile');
  },
  
  updateProfile: (userData: any) => {
    return api.put('/auth/profile', userData);
  },
  
  changePassword: (oldPassword: string, newPassword: string) => {
    return api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword });
  }
};

// User service functions (for admin)
export const UserService = {
  getUsers: (userType?: string) => {
    return api.get('/admin/users', { params: { user_type: userType } });
  },
  
  getUser: (userId: number) => {
    return api.get(`/admin/users/${userId}`);
  },
  
  updateUser: (userId: number, userData: any) => {
    return api.put(`/admin/users/${userId}`, userData);
  },
  
  resetPassword: (userId: number, newPassword: string) => {
    return api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
  },
  
  impersonateUser: (userId: number) => {
    return api.post(`/admin/impersonate/${userId}`);
  },
  
  stopImpersonation: () => {
    return api.post('/admin/stop-impersonation');
  }
};

// Business service functions
export const BusinessService = {
  getCampaigns: () => {
    return api.get('/campaigns');
  },
  
  getCampaign: (campaignId: number) => {
    return api.get(`/campaigns/${campaignId}`);
  },
  
  createCampaign: (campaignData: any) => {
    return api.post('/campaigns', campaignData);
  },
  
  updateCampaign: (campaignId: number, campaignData: any) => {
    return api.put(`/campaigns/${campaignId}`, campaignData);
  },
  
  deleteCampaign: (campaignId: number) => {
    return api.delete(`/campaigns/${campaignId}`);
  },
  
  getCampaignCards: (campaignId: number) => {
    return api.get(`/campaigns/${campaignId}/cards`);
  },
  
  getStorefronts: () => {
    return api.get('/storefronts');
  },
  
  getStorefront: (storefrontId: number) => {
    return api.get(`/storefronts/${storefrontId}`);
  },
  
  createStorefront: (storefrontData: any) => {
    return api.post('/storefronts', storefrontData);
  },
  
  updateStorefront: (storefrontId: number, storefrontData: any) => {
    return api.put(`/storefronts/${storefrontId}`, storefrontData);
  },
  
  getStorefrontInventory: (storefrontId: number) => {
    return api.get(`/storefronts/${storefrontId}/inventory`);
  },
  
  getHostedCards: (storefrontId: number) => {
    return api.get(`/storefronts/${storefrontId}/hosted-cards`);
  },
  
  getOwnCards: (storefrontId: number) => {
    return api.get(`/storefronts/${storefrontId}/own-cards`);
  },

  allCampaigns: () => {
    return new Promise((resolve, reject) => {
      apiCall('/ListSales', null, (res: any) => {
        resolve(res || []);
      }, 'GET');
    });
  }
};

// Affiliate service functions
export const AffiliateService = {
  getCommissions: () => {
    return api.get('/commissions');
  },
  
  getCommissionSummary: () => {
    return api.get('/commissions/summary');
  },
  
  getAffiliatedBusinesses: () => {
    return api.get('/commissions/businesses');
  }
};

// Customer service functions
export const CustomerService = {
  getMyCards: () => {
    return api.get('/gift-cards/my-cards');
  },
  
  getCardDetails: (cardId: number) => {
    return api.get(`/gift-cards/${cardId}`);
  },
  
  getMarketplace: () => {
    return api.get('/marketplace');
  }
  ,
  // Create a new gift card (business/admin endpoint)
  createGiftCard: (payload: {
    campaign_id: number;
    value: number;
    store_id: number;
    code?: string;
    serial_number?: string;
    expires_at?: string; // ISO date string
  }) => {
    return api.post('/gift-cards', payload);
  }
};
